import React, { useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSegment,
  IonSegmentButton,
  IonTitle,
  IonToolbar,
  IonToggle,
  IonSelect,
  IonSelectOption,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonGrid,
  IonRow,
  IonCol,
  IonChip,
  IonCheckbox
} from '@ionic/react';
import {
  colorPaletteOutline,
  closeOutline,
  checkmarkOutline,
  contrastOutline,
  textOutline,
  speedometerOutline,
  accessibilityOutline
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { Theme } from '../types/theme';

interface ThemeSwitcherProps {
  isOpen: boolean;
  onClose: () => void;
}

const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const {
    currentTheme,
    userPreferences,
    availableThemes,
    setTheme,
    updatePreferences,
    resetTheme
  } = useTheme();

  const [selectedTab, setSelectedTab] = useState<'themes' | 'preferences' | 'accessibility'>('themes');

  // Group themes by type
  const lightThemes = availableThemes.filter(theme => !theme.isDark);
  const darkThemes = availableThemes.filter(theme => theme.isDark);
  const customThemes = availableThemes.filter(theme => theme.isCustom);

  const handleThemeSelect = (themeId: string) => {
    setTheme(themeId);
  };

  const handlePreferenceChange = (key: keyof typeof userPreferences, value: any) => {
    updatePreferences({ [key]: value });
  };

  const ThemeCard: React.FC<{ theme: Theme; isSelected: boolean }> = ({ theme, isSelected }) => (
    <IonCard 
      button 
      onClick={() => handleThemeSelect(theme.id)}
      className={`theme-card ${isSelected ? 'selected' : ''}`}
      style={{
        border: isSelected ? `2px solid var(--color-primary)` : '1px solid var(--color-border)',
        position: 'relative'
      }}
    >
      {isSelected && (
        <IonIcon
          icon={checkmarkOutline}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            color: 'var(--color-primary)',
            fontSize: '20px',
            background: 'var(--color-background)',
            borderRadius: '50%',
            padding: '2px'
          }}
        />
      )}
      
      <IonCardHeader style={{ paddingBottom: '8px' }}>
        <IonCardTitle style={{ fontSize: '1rem', marginBottom: '4px' }}>
          {theme.name}
        </IonCardTitle>
        <p style={{ 
          fontSize: '0.875rem', 
          color: 'var(--color-on-surface-variant)',
          margin: 0 
        }}>
          {theme.description}
        </p>
      </IonCardHeader>
      
      <IonCardContent style={{ paddingTop: 0 }}>
        {/* Color preview */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              backgroundColor: theme.colors.primary,
              border: '1px solid var(--color-border)'
            }}
            title="Primary"
          />
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              backgroundColor: theme.colors.secondary,
              border: '1px solid var(--color-border)'
            }}
            title="Secondary"
          />
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              backgroundColor: theme.colors.background,
              border: '1px solid var(--color-border)'
            }}
            title="Background"
          />
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              backgroundColor: theme.colors.surface,
              border: '1px solid var(--color-border)'
            }}
            title="Surface"
          />
        </div>
        
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {theme.isDark && (
            <IonChip style={{ fontSize: '0.75rem', height: '24px' }}>
              {t('theme.dark')}
            </IonChip>
          )}
          {theme.isCustom && (
            <IonChip color="secondary" style={{ fontSize: '0.75rem', height: '24px' }}>
              {t('theme.custom')}
            </IonChip>
          )}
        </div>
      </IonCardContent>
    </IonCard>
  );

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t('theme.customize')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      
      <IonContent>
        <IonSegment 
          value={selectedTab} 
          onIonChange={e => setSelectedTab(e.detail.value as any)}
          style={{ margin: '16px' }}
        >
          <IonSegmentButton value="themes">
            <IonIcon icon={colorPaletteOutline} />
            <IonLabel>{t('theme.themes')}</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="preferences">
            <IonIcon icon={textOutline} />
            <IonLabel>{t('theme.preferences')}</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="accessibility">
            <IonIcon icon={accessibilityOutline} />
            <IonLabel>{t('theme.accessibility')}</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        {selectedTab === 'themes' && (
          <div style={{ padding: '16px' }}>
            {/* Light Themes */}
            <h3 style={{ 
              margin: '0 0 16px 0', 
              color: 'var(--color-on-surface)',
              fontSize: '1.125rem',
              fontWeight: '600'
            }}>
              {t('theme.lightThemes')}
            </h3>
            <IonGrid>
              <IonRow>
                {lightThemes.map(theme => (
                  <IonCol size="12" sizeMd="6" key={theme.id}>
                    <ThemeCard 
                      theme={theme} 
                      isSelected={currentTheme.id === theme.id} 
                    />
                  </IonCol>
                ))}
              </IonRow>
            </IonGrid>

            {/* Dark Themes */}
            {darkThemes.length > 0 && (
              <>
                <h3 style={{ 
                  margin: '24px 0 16px 0', 
                  color: 'var(--color-on-surface)',
                  fontSize: '1.125rem',
                  fontWeight: '600'
                }}>
                  {t('theme.darkThemes')}
                </h3>
                <IonGrid>
                  <IonRow>
                    {darkThemes.map(theme => (
                      <IonCol size="12" sizeMd="6" key={theme.id}>
                        <ThemeCard 
                          theme={theme} 
                          isSelected={currentTheme.id === theme.id} 
                        />
                      </IonCol>
                    ))}
                  </IonRow>
                </IonGrid>
              </>
            )}

            {/* Custom Themes */}
            {customThemes.length > 0 && (
              <>
                <h3 style={{ 
                  margin: '24px 0 16px 0', 
                  color: 'var(--color-on-surface)',
                  fontSize: '1.125rem',
                  fontWeight: '600'
                }}>
                  {t('theme.customThemes')}
                </h3>
                <IonGrid>
                  <IonRow>
                    {customThemes.map(theme => (
                      <IonCol size="12" sizeMd="6" key={theme.id}>
                        <ThemeCard 
                          theme={theme} 
                          isSelected={currentTheme.id === theme.id} 
                        />
                      </IonCol>
                    ))}
                  </IonRow>
                </IonGrid>
              </>
            )}

            {/* Reset Button */}
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <IonButton 
                fill="outline" 
                color="medium"
                onClick={resetTheme}
              >
                {t('theme.resetToDefault')}
              </IonButton>
            </div>
          </div>
        )}

        {selectedTab === 'preferences' && (
          <div style={{ padding: '16px' }}>
            <IonList>
              <IonItem>
                <IonIcon icon={contrastOutline} slot="start" />
                <IonLabel>
                  <h3>{t('theme.darkMode')}</h3>
                  <p>{t('theme.darkModeDescription')}</p>
                </IonLabel>
                <IonToggle
                  checked={userPreferences.darkMode}
                  onIonChange={e => handlePreferenceChange('darkMode', e.detail.checked)}
                  slot="end"
                />
              </IonItem>

              <IonItem>
                <IonIcon icon={textOutline} slot="start" />
                <IonLabel>
                  <h3>{t('theme.fontSize')}</h3>
                  <p>{t('theme.fontSizeDescription')}</p>
                </IonLabel>
                <IonSelect
                  value={userPreferences.fontSize}
                  placeholder={t('theme.selectFontSize')}
                  onIonChange={e => handlePreferenceChange('fontSize', e.detail.value)}
                  slot="end"
                  interface="popover"
                >
                  <IonSelectOption value="small">{t('theme.small')}</IonSelectOption>
                  <IonSelectOption value="medium">{t('theme.medium')}</IonSelectOption>
                  <IonSelectOption value="large">{t('theme.large')}</IonSelectOption>
                </IonSelect>
              </IonItem>

              <IonItem>
                <IonIcon icon={speedometerOutline} slot="start" />
                <IonLabel>
                  <h3>{t('theme.compactMode')}</h3>
                  <p>{t('theme.compactModeDescription')}</p>
                </IonLabel>
                <IonToggle
                  checked={userPreferences.compactMode}
                  onIonChange={e => handlePreferenceChange('compactMode', e.detail.checked)}
                  slot="end"
                />
              </IonItem>
            </IonList>
          </div>
        )}

        {selectedTab === 'accessibility' && (
          <div style={{ padding: '16px' }}>
            <IonList>
              <IonItem>
                <IonIcon icon={accessibilityOutline} slot="start" />
                <IonLabel>
                  <h3>{t('theme.highContrast')}</h3>
                  <p>{t('theme.highContrastDescription')}</p>
                </IonLabel>
                <IonToggle
                  checked={userPreferences.highContrast}
                  onIonChange={e => handlePreferenceChange('highContrast', e.detail.checked)}
                  slot="end"
                />
              </IonItem>

              <IonItem>
                <IonIcon icon={speedometerOutline} slot="start" />
                <IonLabel>
                  <h3>{t('theme.reducedMotion')}</h3>
                  <p>{t('theme.reducedMotionDescription')}</p>
                </IonLabel>
                <IonToggle
                  checked={userPreferences.reducedMotion}
                  onIonChange={e => handlePreferenceChange('reducedMotion', e.detail.checked)}
                  slot="end"
                />
              </IonItem>
            </IonList>

            {/* Information about accessibility features */}
            <IonCard style={{ margin: '16px 0' }}>
              <IonCardHeader>
                <IonCardTitle style={{ fontSize: '1rem' }}>
                  {t('theme.accessibilityInfo')}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <p style={{ margin: 0, fontSize: '0.875rem' }}>
                  {t('theme.accessibilityInfoDescription')}
                </p>
              </IonCardContent>
            </IonCard>
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default ThemeSwitcher; 